import { memo, type CSSProperties } from 'react'
import { AlertTriangle, Ban, Bell, CalendarClock, Clock3, ListTodo, TrendingUp } from 'lucide-react'
import { formatDealValue, getDealAvatarFallback } from '@/utils/dealDisplay'
import { cn } from '@/utils/cn'
import { Tooltip } from '@/components/ui/Tooltip'
import { KanbanMoveStageMenu } from '@/components/Deals/KanbanMoveStageMenu'
import { resolveDealCardRisk } from '@/components/Deals/dealCardRisk.util'
import { getDirectProposalPendingAlert } from '@/components/Deals/directProposalJourneyAlert.util'
import { getDealJourneyStageLabel } from '@/components/Deals/dealsJourney.util'
import { getTesteGratisTiming } from '@/components/Deals/testeGratisJourney.util'
import {
  getDealMeetingSchedule,
  getNextStepV2,
  type DealRiskIconKind,
} from '@/components/DealsV2/kanbanCardV2Utils'
import type { Deal, DealStage } from '@/types'

function RiskIcon({ kind }: { kind: DealRiskIconKind }) {
  const props = { size: 12 as const }
  switch (kind) {
    case 'ban':
      return <Ban {...props} />
    case 'clock':
      return <Clock3 {...props} />
    case 'alert':
      return <AlertTriangle {...props} />
    case 'trending':
      return <TrendingUp {...props} />
  }
}

export type DealsJourneyRowProps = {
  deal: Deal
  journeyColor: string
  stageColor: string
  onClick: () => void
  areValuesVisible?: boolean
  onMoveStage?: (dealId: string, newStage: DealStage) => void
}

function DealsJourneyRowComponent({
  deal,
  journeyColor,
  stageColor,
  onClick,
  areValuesVisible = false,
  onMoveStage,
}: DealsJourneyRowProps) {
  const proposalAlert = getDirectProposalPendingAlert(deal)
  const risk = resolveDealCardRisk(deal)
  const meeting = getDealMeetingSchedule(deal)
  const nextStep = getNextStepV2(deal)
  const stageLabel = getDealJourneyStageLabel(deal)
  const driftLabel = deal.driftDays > 0 ? `${deal.driftDays}d` : 'Recente'
  const trial = getTesteGratisTiming(deal)

  return (
    <article
      onClick={onClick}
      style={{ '--stage-color': journeyColor } as CSSProperties}
      className={cn(
        'deals-journey-row group/row rounded-lg border px-2.5 py-2 transition-[border-color,background] duration-150',
        onMoveStage ? 'cursor-grab' : 'cursor-pointer',
        risk.tone === 'info' &&
          'border-[var(--brand-primary)]/30 !bg-[var(--brand-primary)]/[0.06]',
        risk.tone === 'warning' && 'border-warning/30',
        risk.tone === 'danger' && 'border-danger/30',
      )}
    >
      <div className='flex items-center justify-between gap-2'>
        <p className='min-w-0 truncate text-[13px] font-semibold leading-tight text-text-primary'>
          {deal.empresaNome}
        </p>
        <div className='flex shrink-0 items-center gap-0.5'>
          {onMoveStage ? <KanbanMoveStageMenu deal={deal} onMoveStage={onMoveStage} /> : null}
          {risk.tone && risk.icon ? (
            <Tooltip content={risk.label} position='bottom'>
              <span
                className={cn(
                  'inline-flex h-5 w-5 items-center justify-center rounded',
                  risk.tone === 'danger'
                    ? 'text-danger'
                    : risk.tone === 'info'
                      ? 'text-[var(--brand-primary)]'
                      : 'text-warning',
                )}
              >
                {deal.financeAwaitingCloser ? <Bell size={11} /> : <RiskIcon kind={risk.icon} />}
              </span>
            </Tooltip>
          ) : null}
          <Tooltip content={deal.closerNome || 'Sem closer'} position='bottom'>
            <span className='inline-flex h-6 w-6 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--bg-dark)] text-[8px] font-bold uppercase text-text-secondary'>
              {getDealAvatarFallback(deal.closerNome, deal.closerAvatar)}
            </span>
          </Tooltip>
        </div>
      </div>

      <div className='mt-1 flex items-center justify-between gap-2'>
        <span className='inline-flex min-w-0 items-center gap-1 text-[10px] text-text-muted'>
          <span
            className='h-1.5 w-1.5 shrink-0 rounded-full'
            style={{ backgroundColor: stageColor }}
            aria-hidden
          />
          <span className='truncate'>{stageLabel}</span>
        </span>
        <span className='shrink-0 text-[11px] font-bold tabular-nums text-text-primary'>
          {formatDealValue(deal.valor, areValuesVisible, true)}
        </span>
      </div>

      {proposalAlert ? (
        <p
          className={cn(
            'mt-1 truncate text-[10px] font-medium',
            proposalAlert.tone === 'danger' ? 'text-danger' : 'text-warning',
          )}
        >
          {proposalAlert.label}
        </p>
      ) : null}

      {trial ? (
        <div className='mt-1.5 grid grid-cols-2 gap-1.5'>
          <div className='rounded-md border border-[var(--glass-border)] px-2 py-1'>
            <p className='text-[9px] font-medium uppercase tracking-wide text-text-muted'>Entrou</p>
            <p className='text-[11px] font-semibold tabular-nums text-text-primary'>{trial.enteredLabel}</p>
          </div>
          <div className={cn(
            'rounded-md border px-2 py-1',
            trial.overdue ? 'border-danger/40' : 'border-[var(--glass-border)]',
          )}>
            <p className='text-[9px] font-medium uppercase tracking-wide text-text-muted'>{trial.remainingCaption}</p>
            <p className={cn(
              'text-[11px] font-semibold tabular-nums',
              trial.overdue ? 'text-danger' : 'text-text-primary',
            )}>
              {trial.remainingLabel}
            </p>
          </div>
        </div>
      ) : (
      <div className='mt-1 flex items-center justify-between gap-2 text-[10px]'>
        <span
          className={cn(
            'shrink-0 font-medium tabular-nums',
            deal.stagnant || deal.driftDays >= 5 ? 'text-warning' : 'text-text-muted',
          )}
        >
          {driftLabel}
        </span>
        {meeting ? (
          <span
            className={cn(
              'inline-flex min-w-0 items-center justify-end gap-1 truncate font-medium',
              proposalAlert?.tone === 'danger'
                ? 'text-danger'
                : proposalAlert
                  ? 'text-warning'
                  : 'text-accent',
            )}
          >
            <CalendarClock size={10} aria-hidden />
            <span className='truncate'>
              Reunião · {meeting.date} {meeting.time}
            </span>
          </span>
        ) : (
          <span className='inline-flex min-w-0 items-center justify-end gap-1 truncate text-text-muted'>
            <ListTodo size={10} aria-hidden />
            <span className='truncate'>{nextStep}</span>
          </span>
        )}
      </div>
      )}
    </article>
  )
}

export const DealsJourneyRow = memo(DealsJourneyRowComponent)
